import clsx from 'clsx';
import { FunctionComponent, useState } from 'react';
import { useIsMounted } from 'usehooks-ts';
import {
  ACTIVELIKE_ORG_STATUSES,
  ACTIVELIKE_USER_STATUSES,
  AdminQueryUser,
  cancelSubscription,
  deleteUser,
  setUserVerified,
} from '../../../api/admin';
import { stringifyError } from '../../../utils/core';
import AdminAction, { AdminActionProps } from './AdminAction';

type AdminActionsForUserProps = {
  user: AdminQueryUser;
  requeryUser: () => Promise<void>;
};

const AdminActionsForUser: FunctionComponent<AdminActionsForUserProps> = ({
  user,
  requeryUser,
}) => {
  const getIsMounted = useIsMounted();

  const [isLoading, setIsLoading] = useState(false);

  const { cognito, dbUser } = user;

  const userName =
    cognito?.attrs.email || dbUser?.email || dbUser?.userId || 'unknown';

  const actions: AdminActionProps[] = [];

  if (
    cognito &&
    (cognito.userStatus !== 'CONFIRMED' || !cognito.attrs.email_verified)
  ) {
    actions.push({
      text: 'Verify user',
      onClick: (evt) => {
        evt.preventDefault();
        const confirmActual = confirm(
          `Are sure you want to confirm ${userName}?`,
        );

        if (confirmActual) {
          setIsLoading(true);

          setUserVerified(cognito.attrs.sub)
            .then(() => {
              return requeryUser().catch((err) => {
                alert(
                  `User verified, but error reloading. Please referesh the page: ${stringifyError(err)}`,
                );
              });
            })
            .catch((err) => {
              alert(`Error verifying user: ${stringifyError(err)}`);
            })
            .finally(() => {
              if (getIsMounted()) {
                setIsLoading(false);
              }
            });
        }
      },
    });
  }

  if (cognito) {
    actions.push({
      text: 'Delete user',
      onClick: (evt) => {
        evt.preventDefault();
        const confirmExpected = 'DELETE';
        const confirmActual = prompt(
          `Are sure you want to delete ${userName}. Type "${confirmExpected}" to confirm.`,
        );

        if (confirmExpected === confirmActual) {
          setIsLoading(true);

          deleteUser(cognito.attrs.sub)
            .then((resp) => {
              console.log('DELETED!!', resp);
              if (resp.type === 'success') {
                prompt(
                  `Success: copy text below...`,
                  resp.message || 'unknown',
                );
              } else {
                prompt(`Error: copy text below...`, resp.message || 'unknown');
              }
              return requeryUser().catch((err) => {
                alert(
                  `Error reloading user. Please referesh the page: ${stringifyError(err)}`,
                );
              });
            })
            .catch((err) => {
              alert(`Error deleting user: ${stringifyError(err)}`);
            })
            .finally(() => {
              if (getIsMounted()) {
                setIsLoading(false);
              }
            });
        }
      },
    });
  }

  // TODO - clean up logic for "can delete sub"
  const subscriptionId = dbUser?.subscriptionId;
  if (
    subscriptionId &&
    dbUser?.status &&
    ACTIVELIKE_USER_STATUSES.includes(dbUser.status) &&
    (!dbUser.orgId ||
      (dbUser.orgStatus &&
        ACTIVELIKE_ORG_STATUSES.includes(dbUser.orgStatus) &&
        dbUser.orgRole !== 'admin'))
  ) {
    actions.push({
      text: 'Cancel subscription',
      onClick: (evt) => {
        evt.preventDefault();
        const confirmExpected = 'CANCEL';
        const confirmActual = prompt(
          `Are sure you want to cancel this user's ${dbUser.status} subscription?. Type "${confirmExpected}" to confirm.`,
        );

        if (confirmExpected === confirmActual) {
          setIsLoading(true);

          cancelSubscription({
            userId: dbUser.userId,
            subscriptionId,
          })
            .then((resp) => {
              console.log('DELETED!!', resp);
              if (resp.type === 'error') {
                alert(`Error canceling subscription: ${resp.message}`);
              }
              return requeryUser().catch((err) => {
                alert(
                  `Error canceling subscription. Please referesh the page: ${stringifyError(err)}`,
                );
              });
            })
            .catch((err) => {
              alert(`Error canceling user: ${stringifyError(err)}`);
            })
            .finally(() => {
              if (getIsMounted()) {
                setIsLoading(false);
              }
            });
        }
      },
    });
  }

  return actions.length ? (
    <div
      className={clsx(
        'flex flex-wrap items-center justify-start gap-x-4 gap-y-2 rounded-lg bg-gray-100 p-5',
        isLoading && 'pointer-events-none opacity-50',
      )}
    >
      <div className="text-sm font-medium text-gray-900">Actions:</div>
      {actions.map((action) => (
        <AdminAction key={action.text} {...action} />
      ))}
    </div>
  ) : null;
};

AdminActionsForUser.displayName = 'AdminActionsForUser';

export default AdminActionsForUser;
